// Risk score calculation utility
// Calculates the composite risk score based on current and target scores

/**
 * Calculate the composite risk score for a quality aspect.
 * @param {number} currentScore - The current score (voteWhereWeAre)
 * @param {number} targetScore - The target score (voteWhereWantToBe)
 * @param {number} [probability=2] - Optional probability factor (default 2)
 * @returns {number} Composite risk score
 */
export function calculateCompositeRiskScore(currentScore, targetScore, probability = 2) {
  const gap = Math.abs(targetScore - currentScore);
  const gapWeight = 0.5;
  const severityWeight = 0.5;

  // Calculate severity based on current score, target, and gap
  let severity;
  if ((currentScore < 3 && gap > 2) || gap > 3) {
    severity = 1; // High severity
  } else if ((targetScore < 3 && gap <= 2) || (currentScore >= 5 && gap <= 1)) {
    severity = 0; // Low severity
  } else {
    severity = 0.5; // Medium severity
  }

  const maxPossibleGap = 8;
  const gapImpact = Math.min(gap / maxPossibleGap, 1);

  const compositeScore = ((gapWeight * gapImpact) + (severityWeight * severity)) * 8;

  return compositeScore * (probability / 2);
}

/**
 * Process all voted aspects from results for risk score calculation.
 * @param {object} results - The results object from Firebase (id -> data)
 * @param {object} segmentRiskData - The risk data (defaultRisks, etc.)
 * @param {object} [aspectIdToMainCategory] - Optional mapping from aspect id to main category
 * @param {object} [aspectIdToName] - Optional mapping from aspect id to name
 * @returns {Array} Array of processed aspect objects
 */
export function processAllVotedAspects(results, segmentRiskData, aspectIdToMainCategory = {}, aspectIdToName = {}) {
  if (!results) return [];
  const resultsArray = Object.entries(results).map(([id, data]) => ({ id, ...data }));
  return resultsArray.map(resultData => {
    const stringTopicId = String(resultData.id);
    // Find all risks associated with this quality aspect
    const associatedRisks = segmentRiskData.defaultRisks.filter(risk => 
      risk.qualityAspects?.includes(stringTopicId)
    );
    // Calculate scores and gap
    const currentScore = Number(resultData.voteWhereWeAre) || 0;
    const targetScore = Number(resultData.voteWhereWantToBe) || 0;
    const gap = Math.abs(targetScore - currentScore);
    // Use category mapping if provided
    const category = aspectIdToMainCategory[stringTopicId] || 'Unknown';
    const name = aspectIdToName[stringTopicId] || stringTopicId;
    const highestRisk = associatedRisks.reduce((highest, risk) => {
      const riskLevels = { Low: 1, Medium: 2, High: 3 };
      return (riskLevels[risk.impact] || 0) > (riskLevels[highest.impact] || 0) ? risk : highest;
    }, { impact: 'Low', category });
    const compositeScore = calculateCompositeRiskScore(currentScore, targetScore);
    const riskLevel = compositeScore > 5 ? 'High' : compositeScore >= 3 ? 'Medium' : 'Low';
    return {
      id: stringTopicId,
      name,
      currentScore,
      targetScore,
      gap,
      riskLevel,
      compositeScore,
      category,
      impact: highestRisk.impact,
      impactAreas: [...new Set(associatedRisks.map(risk => risk.category))],
      recommendations: associatedRisks.flatMap(risk => 
        risk.mitigationQualityImprovements
          ?.filter(imp => imp?.aspectId === stringTopicId)
          ?.map(imp => imp.improvement)
          .filter(Boolean) || []
      ),
      risks: associatedRisks
    };
  });
} 